import { Component, Input } from '@angular/core';
import moment from 'moment';
import { DeviceService } from '../../device.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-power-study-soc-chart',
	templateUrl: './power-study-soc-chart.component.html',
	styleUrls: ['./power-study-soc-chart.component.css']
})
export class PowerStudySocChartComponent {
	@Input() minDeviceDate: any = null;
	@Input() maxDeviceDate: any = null;
	@Input() workingDays: string = null;
	@Input() studyInfo: any = {};
	@Input() device: any = [];
	@Input() useParentFilters: boolean = false;
	@Input() socData: any[] | undefined = undefined;
	@Input() tooltipKey: string = '';
	@Input() haveZoom: boolean = false;

	rawSOCData: any[] = [];
	macAddress: string | null = null;
	show20PercentLine: boolean = true;
	show80PercentLine: boolean = true;
	percentValueOf20 = 20;
	percentValueOf80 = 80;

	// chart
	chartObject: any = {
		formatterType: 'DateFormat',
		formatter: {pattern: "MM/dd/yyyy HH:mm:ss"},
		columnNames: [
			'Date',
			'End SOC',
			'20% Threshold',
			'80% Threshold'
		],
		config: {
			orientation: 'horizontal',
			vAxis: {
				title: this.translate.instant('perf_analytic.soc_percent'),
			},
			hAxis: {
				title: this.translate.instant('g.time'),
				format: "MM/dd/yyyy HH:mm:ss",
			},
			chartArea: {
				bottom: 170,
			},
			explorer: {
				axis: 'horizontal',
				keepInBounds: true,
				maxZoomIn: 0.1,
			},
			series: {
				0: {
					visibleInLegend: true,
					labelInLegend: 'End SOC',
					color: '#4285F4',
					lineWidth: 2,
				},
				1: {
					visibleInLegend: false,
					color: '#FF0000',
					lineDashStyle: [6, 4],
					enableInteractivity: false,
				},
				2: {
					visibleInLegend: false,
					color: '#00AA00',
					lineDashStyle: [6, 4],
					enableInteractivity: false,
				},
			},
		},
	};

	// date data
	deviceInstallationDate: any = moment(0).toDate();
	fromDate: Date = moment().subtract(3, 'months').toDate();
	toDate: Date = moment().subtract(1, 'day').toDate();
	maxDate = moment().subtract(1, 'day').toDate();
	invalidDateRange: boolean = false;

	constructor(
		private deviceService: DeviceService,
		private translate: TranslateService
	) { }

	ngOnInit() {
		if (this.maxDeviceDate && this.maxDeviceDate < this.maxDate) {
			this.maxDate = this.maxDeviceDate;
			this.toDate = this.maxDeviceDate;
		}
	}

	ngOnChanges(changes) {
		if (changes.socData && this.socData !== undefined) {
			this.rawSOCData = this.socData;
			this.prepareChartData(this.socData);
			return;
		}

		this.deviceInstallationDate = moment(0).toDate();
		this.fromDate = moment().subtract(3, 'months').toDate();
		this.maxDate = new Date(new Date().setDate(new Date().getDate() - 1));
		if (this.studyInfo?.id && !this.useParentFilters)
			this.handleStudyDeviceStartEndDate();

		if(changes.device) {
			if (this.maxDeviceDate && this.maxDeviceDate < this.maxDate) {
				this.maxDate = this.maxDeviceDate;
				this.toDate = this.maxDeviceDate;
			}

			if (!this.device.config_info.is_charglink)
				this.macAddress = this.device?.config_info?.mac_address;

			this.updateInstallationDate((this.device.config_info.installation_date || 0));

			if (this.useParentFilters) {
				if (this.minDeviceDate)
					this.fromDate = this.minDeviceDate;

				if (this.maxDeviceDate)
					this.toDate = this.maxDeviceDate;
			}

			this.getSOCData();
		}

		if (this.useParentFilters && (changes.minDeviceDate || changes.maxDeviceDate || changes.workingDays)) {
			if (this.minDeviceDate)
				this.fromDate = this.minDeviceDate;

			if (this.maxDeviceDate)
				this.toDate = this.maxDeviceDate;

			this.getSOCData();
		}
	}

	DatesChanged() {
		this.invalidDateRange = false;
		if(moment(this.fromDate).unix() > moment(this.toDate).unix())
			return this.invalidDateRange = true;

		this.getSOCData();
	}

	handleStudyDeviceStartEndDate() {
		this.deviceInstallationDate = new Date(this.studyInfo.start_study_time*1000);
		this.maxDate = this.studyInfo.is_done ? new Date(this.studyInfo.end_study_time*1000) : new Date(new Date().setDate(new Date().getDate()));
		this.fromDate = this.deviceInstallationDate;
		this.toDate = this.maxDate;
		this.getSOCData();
	}

	updateInstallationDate(date) {
		if (date > 0)
			this.deviceInstallationDate = moment(date*1000).toDate();

		if (this.minDeviceDate && moment(this.minDeviceDate).unix() > moment(this.deviceInstallationDate).unix())
			this.deviceInstallationDate = this.minDeviceDate;

		if(moment(this.fromDate).unix() < moment(this.deviceInstallationDate).unix())
			this.fromDate = this.deviceInstallationDate;
	}

	get studyStartedToday(): boolean {
		return !!(this.studyInfo?.id && moment.unix(this.studyInfo.start_study_time).isSame(moment(), 'day'));
	}

	getSOCData() {
		if (this.socData !== undefined)
			return;

		if (!this.macAddress)
			return;

		if (this.studyStartedToday)
			return this.chartObject.data = null;

		let zoneDiff = new Date().getTimezoneOffset() * -1;
		let from:any = new Date(new Date(this.fromDate).setHours(0, 0, 0, 0));
		let to:any = new Date(new Date(this.toDate).setHours(23, 59, 59, 999));
		from = new Date(from.getTime() + (zoneDiff * 60 * 1000));
		to = new Date(to.getTime() + (zoneDiff * 60 * 1000));

		from = moment(from).utc().startOf('day').unix();
		to = moment(to).utc().endOf('day').unix();

		const workingDaysOnly = this.workingDays === 'workingDays';
		this.deviceService.getDeviceSOCData(this.macAddress, this.studyInfo.id, from, to, workingDaysOnly).subscribe((res: any) => {
			this.rawSOCData = res.socData || [];
			this.prepareChartData(this.rawSOCData);
		})
	}

	prepareChartData(data) {
		if (!data || !data.length)
			return this.chartObject.data = [];

		const columnNames: string[] = ['Date', 'End SOC'];
		const series: any = {
			0: {
				visibleInLegend: true,
				labelInLegend: 'End SOC',
				color: '#4285F4',
				lineWidth: 2,
			}
		};

		let seriesIndex = 1;

		if (this.show20PercentLine) {
			columnNames.push('20% Threshold');
			series[seriesIndex++] = {
				visibleInLegend: false,
				color: '#FF0000',
				lineDashStyle: [6, 4],
				enableInteractivity: false,
			};
		}

		if (this.show80PercentLine) {
			columnNames.push('80% Threshold');
			series[seriesIndex++] = {
				visibleInLegend: false,
				color: '#00AA00',
				lineDashStyle: [6, 4],
				enableInteractivity: false,
			};
		}

		this.chartObject.columnNames = columnNames;
		this.chartObject.config.series = series;

		const chartData = data.map((item) => {
			const row: any[] = [
				new Date(moment.utc(item.timestamp * 1000).format('YYYY-MM-DD HH:mm:ss')),
				item.soc  ? +(item.soc).toFixed(2) : 0,
			];

			if (this.show20PercentLine) 
				row.push(this.percentValueOf20);
			if (this.show80PercentLine) 
				row.push(this.percentValueOf80);

			return row;
		});

		this.chartObject.data = chartData;
	}
}
